import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import Input from "../../../../../common/src/components/widget/Input";
import Button from "../../../../../common/src/components/widget/Button";
import Checkbox from "../../../../../common/src/components/widget/Checkbox";

import * as yup from "yup";
import FormContainer from "common/src/components/form/FormContainer";
import {useForm} from "common/src/hooks/form/formHook";
import {addErrorNotification, addSuccessNotification} from "../../../state/notification/notifications";
import FloatingLabelTagsWrapper from "common/src/components/widget/FloatingLabelTagsWrapper";
import {deleteUser, fetchUserDetails, resendUserActivationEmail, saveUser} from "../../../state/api/userAPI";
import {clearInfiniteScroll, fetchGroupPage} from "../../../state/api/groupAPI";
import {hasRole} from "../../../utils/userUtils";
import {Role} from "../../../auth/roles";
import {UserContext} from "../../../context/UserContext";
import Select from "common/src/components/widget/Select";
import {getKey} from "../../../utils/InfiniteScroleUtil";
import {constructFilter} from "common/src/utils/AdvancedFilterApiUtils";
import {constructSort} from "common/src/utils/APIUtils";
import {isEqual} from "lodash";
import Fragment from "common/src/components/widget/Fragment";
import Modal from "common/src/components/widget/Modal";

function UserForm() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {user, _} = useContext(UserContext);

    const {userId} = useParams();
    const edit = typeof (userId) !== "undefined";
    console.log("userId: ", userId);
    const userDetails = useSelector(state => state.userAPI[userId]);
    const [isBusy, setIsBusy] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showResendConfirm, setShowResendConfirm] = useState(false);
    const dispatch = useDispatch();

    const [filterString, setFilterString] = useState(getKey(constructFilter([])));
    const [sortString, setSortString] = useState(getKey(constructSort({ name: "asc" })));
    const total = useSelector(state => state.groupAPI.statesInfinitiveScrolls?.[filterString]?.totalElements);
    const totalPages = useSelector(state => state.groupAPI.statesInfinitiveScrolls?.[filterString]?.totalPages);

    const groupOptions = useSelector(state => state.groupAPI.statesInfinitiveScrolls?.[filterString]?.[sortString]?.content);
    const loadingGroups = useSelector(state => state.groupAPI.loading);
    const currentOptionsPage = useRef(-1);
    const optionsFilter = useRef(null);
    const [options, setOptions] = useState([]);

    const schema = {
        username: yup.string().max(255, t('length_exceeded_error')).required(),
        firstName: yup.string().max(255, t('length_exceeded_error')).required(),
        lastName: yup.string().max(255, t('length_exceeded_error')).required(),
        email: yup.string().email().required()
    };
    const initialValues = {
            firstName: null,
            lastName: null, email: null, emailVerified: false,
            username: null, groups: [], enabled: true
        }

    useEffect(() => {
        userId && setIsBusy(true);
        userId && dispatch(fetchUserDetails({userId}));
    }, [userId]);

    useEffect(() => {
        if (userDetails) {
            if (isBusy) {
                setIsBusy(false);
            }
            setValues(userDetails);
            setFormNotDirty()
        } else if (!userId && !isBusy) {
            setValues(initialValues);
            setFormNotDirty()
        }
        return () => {
            // userId && userDetails && dispatch(cleanupUserById(userId));
        }
    }, [userDetails]);

    const {
        values,
        getField,
        setFormNotDirty,
        handleSubmit,
        setValues,
        isEdited
    } = useForm(submitUser, {}, schema);

    async function submitUser() {
        if (!hasRole(user, Role.ADMIN_USERS)) {
            dispatch(addErrorNotification(
                {title: t(`toast.error`), content: t(`toast.insufficient_permissions`)})
            );
            return;
        }
        setIsBusy(true);
        const requestData = {
            id: values.id,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            emailVerified: values.emailVerified,
            username: values.username,
            enabled: values.enabled,
            groups: values.groups
        };

        const successMessage = t(`toast.success_${edit ? 'edit' : 'add'}_user`);

        await dispatch(saveUser(requestData)).unwrap()
            .then(
                (_) => {
                    setFormNotDirty();
                    dispatch(addSuccessNotification({title: t('toast.success'), content: successMessage}));
                    navigate("/users");
                }
            ).catch(
                (error) => {
                    dispatch(addErrorNotification(
                        {
                            title: t(`toast.error`),
                            content: t(`toast.error_${edit ? 'edit' : 'add'}_group`)
                        }));
                }
            ).finally(
                () => {
                    setIsBusy(false);
                }
            )

    }

    function handleResendActivationEmail() {
        if (!hasRole(user, Role.ADMIN_USERS)) {
            dispatch(addErrorNotification(
                {title: t(`toast.error`), content: t(`toast.insufficient_permissions`)})
            );
            return;
        }
        setShowResendConfirm(true);
    }

    async function handleConfirmedResend() {
        setIsBusy(true);
        await dispatch(resendUserActivationEmail(values.id)).unwrap()
            .then(
                (_) => {
                    dispatch(addSuccessNotification(
                        {title: t('toast.success'), content: t('toast.success_resend_activation_user')})
                    );
                    setShowResendConfirm(false);
                }
            ).catch(
                (error) => {
                    dispatch(addErrorNotification(
                        {title: t('toast.error'), content: t(error?.message ?? 'toast.error_resend_activation_user')})
                    );
                }
            ).finally(
                () => {
                    setIsBusy(false);
                }
            );
    }

    const handleDeleteUser = () => {
        if (!hasRole(user, Role.ADMIN_USERS)) {
            dispatch(addErrorNotification(
                {title: t(`toast.error`), content: t(`toast.insufficient_permissions`)})
            );
            return;
        }
        setShowDeleteConfirm(true);
    }

    const handleConfirmedDelete = () => {
        setIsBusy(true);
        return dispatch(deleteUser(values.id)).unwrap()
            .then(
                (_) => {
                    dispatch(addSuccessNotification(
                        {title: t('toast.success'), content: t('toast.success_delete_user')})
                    );
                    setShowDeleteConfirm(false);
                    navigate("/users");
                }
            ).catch(
                (error) => {
                    dispatch(addErrorNotification(
                        {title: t('toast.error'), content: t(error?.message ?? 'toast.error_delete_user')})
                    );
                }
            ).finally(
                () => {
                    setIsBusy(false);
                }
            );
    }

    const handleGroupChange = (group, isSelected) => {

        if (isSelected) {
            const valueTemp = [...getField("groups").value, {...group}];
            getField("groups").setValue([...valueTemp], null, true);
            setValues({...values, "groups": [...valueTemp]})

        } else {

            const valueTemp =
                [...[...getField("groups").value].filter(item => item.id !== group.id)];

            getField("groups").setValue([...valueTemp], null, true);
            setValues({...values, "groups": [...valueTemp]})
        }

    }

    const handleTagRemove = (tagToRemove) => {
        handleGroupChange(tagToRemove, false)
    };
    useEffect(
        () => {
            dispatch(clearInfiniteScroll());
        }, []);
    useEffect(() => {
        if (!loadingGroups) {
            setOptions(groupOptions ?? []);
        }
    }, [total, totalPages, groupOptions, loadingGroups]);

    const onSearchQueryChange = useCallback((query) => {
        if (!isEqual(query, optionsFilter.current)) {
            optionsFilter.current = query;
            currentOptionsPage.current = 0;
        }

        const filter = query && query.trim() !== "" ? [{
            name: "name",
            operator: "LIKE_IC",
            value: `${query.trim()}`
        }] : [];
        const filterStringInternal = getKey(constructFilter(filter));
        const sortString = getKey(constructSort({ "name": "asc" }));
        setSortString(sortString);

        if (filterStringInternal && filterStringInternal !== "") {
            setFilterString(filterStringInternal);
        } else {
            setFilterString("blank");
        }
        const pageMod = totalPages || 1;
        console.log("pageMod", pageMod);
        dispatch(fetchGroupPage({
            page: {
                page: (currentOptionsPage.current % pageMod),
                size: 10,
            },
            sort: { "name": "asc" },
            filter
        }));
    }, [totalPages]);

    const onFetchNextPage = () => {
        if (!loadingGroups) {
            currentOptionsPage.current += 1;
            onSearchQueryChange(optionsFilter.current);
        }
    };
    return (
        <FormContainer onSubmit={handleSubmit} isEdited={isEdited}>
            <div className="card">
                <header className="card-header">
                    <p className="card-header-title">
                        <span className="icon fz fz-users medium mr-1"/>
                        <span>{t('user.manage')}</span>
                    </p>
                </header>
                <div className="card-content">


                    <div className="columns is-multiline">
                        <div className="column is-half-tablet">
                            <div className="columns is-multiline">

                                <div className="column is-half-tablet">
                                    <Input label={t('user.firstName')} formField={getField("firstName")}/>
                                </div>
                                <div className="column is-half-tablet">
                                    <Input label={t('user.lastName')} formField={getField("lastName")}/>
                                </div>
                            </div>

                            <div className="columns is-multiline">
                                <div className="column is-half-tablet">
                                    <Input label={t('user.username')} formField={getField("username")}
                                           disabled={values.id != null}/>
                                </div>
                                <div className="column is-half-tablet">
                                    <Input label={t('user.email')} formField={getField("email")} type="email"/>
                                </div>
                            </div>

                            <div className="columns is-multiline">
                                <div className="column is-half-tablet">
                                    <Checkbox label={t('user.enabled')}
                                              disabled={values.id == null}
                                              formField={getField('enabled')}/>
                                </div>
                            </div>
                        </div>


                        <div className="column is-one-quarter-tablet">
                            <>
                                <Select label={t('label.dataset.add_groups')}
                                        isMultiSelect={true}
                                        formField={getField("groups")}
                                        onSearchQueryChange={onSearchQueryChange}
                                        loading={loadingGroups}
                                        fetchMoreOptions={onFetchNextPage}
                                        hasMoreOptions={(total == null || (total > 0 && (total > options.length)))}
                                        options={[...(options || [])]}
                                        optionsCode={"id"}
                                        optionsValue={"id"}
                                        optionsLabel={"name"}
                                        isSelectionAnObject={true}
                                        emptyOption={false}/>
                            </>

                        </div>
                        <div className="column is-one-quarter-tablet">
                            {
                                getField("groups") && getField("groups").value &&
                                <FloatingLabelTagsWrapper
                                    label="Groups"
                                    tags={[...getField("groups").value]}
                                    formatter={(tag) => tag.name}
                                    tooltipFormatter={(tag) => tag.description}
                                    onTagRemove={handleTagRemove}
                                />
                            }
                        </div>
                    </div>
                </div>
            </div>
            <Button isSubmit={true} color="primary" label={edit ? t('save') : t('submit')} isLoading={isBusy}/>
            <Button color="primary" isOutlined={true} label={t('cancel')} onClick={() => navigate("/users")}/>
            <Fragment rendered={values?.id != null && userDetails?.emailVerified === false && userDetails?.enabled === true}>
                <Button onClick={() => handleResendActivationEmail()} color="primary" isOutlined={true}
                        isLoading={isBusy} label={t('user.resend_activation')}/>
            </Fragment>
            <Fragment rendered={values?.id != null && userDetails?.enabled === false}>
                <Button onClick={() => handleDeleteUser()} color="danger" className={"is-outlined"}
                        label={t('delete')}/>
            </Fragment>
            <Modal title={t('user.delete_confirm_title')}
                   content={t('user.delete_confirm')}
                   showPopup={showDeleteConfirm}
                   stayLabel={t('cancel')}
                   leaveLabel={t('delete')}
                   confirmButtonColor={'danger'}
                   onClose={() => setShowDeleteConfirm(false)}
                   onDelete={() => handleConfirmedDelete()}>
            </Modal>
            <Modal title={t('user.resend_confirm_title')}
                   content={t('user.resend_confirm')}
                   showPopup={showResendConfirm}
                   stayLabel={t('cancel')}
                   leaveLabel={t('user.resend_activation')}
                   confirmButtonColor={'primary'}
                   onClose={() => setShowResendConfirm(false)}
                   onDelete={() => handleConfirmedResend()}>
            </Modal>
        </FormContainer>
    );
}

export default UserForm;
